/**
 * ManuallyCPD.js — Manually record CPD points modal.
 */

import React, { useState, useEffect } from 'react';
import Select from 'react-select';
import { Button, Modal, Alert, Form, Col } from 'react-bootstrap';
import axios from 'axios';
import CalenderIcon from '../../images/program.svg';

export const ManuallyCPD = (props) => {
    const [TrainingProvider, setTrainingProvider] = useState("");
    const [ProgramName,      setProgramName]      = useState("");
    const [ProgrameFormat,   setProgrameFormat]   = useState("");
    const [ProgrameDate,     setProgrameDate]     = useState("");
    // Point categories default to 0 — the user only needs to raise the one(s)
    // that apply; at least one must be greater than 0 (validated on submit).
    const [PS, setPS] = useState('0');
    const [PM, setPM] = useState('0');
    const [SL, setSL] = useState('0');
    const [EP, setEP] = useState('0');

    const [SuccessMessage,    setSuccessMessage]    = useState(undefined);
    const [ErrorMessage,      setErrorMessage]      = useState(undefined);
    const [SomethingUpadating,setSomethingUpadating]= useState(false);

    const [TrainingProvider_ER, setTrainingProvider_ER] = useState(false);
    const [ProgramName_ER,      setProgramName_ER]      = useState(false);
    const [ProgrameFormat_ER,   setProgrameFormat_ER]   = useState(false);
    const [ProgrameDate_ER,     setProgrameDate_ER]     = useState(false);
    // Single group-level error: true when none of the 4 categories is > 0.
    const [Points_ER, setPoints_ER] = useState(false);

    /**
     * Reset the form (fields, point values, validation errors, status messages)
     * each time the modal closes, so reopening always shows a clean form rather
     * than the previous values / error messages.
     */
    useEffect(() => {
        if (!props.cdp_point_popup) {
            setTrainingProvider("");
            setProgramName("");
            setProgrameFormat("");
            setProgrameDate("");
            setPS('0');
            setPM('0');
            setSL('0');
            setEP('0');
            setSuccessMessage(undefined);
            setErrorMessage(undefined);
            setSomethingUpadating(false);
            setTrainingProvider_ER(false);
            setProgramName_ER(false);
            setProgrameFormat_ER(false);
            setProgrameDate_ER(false);
            setPoints_ER(false);
        }
    }, [props.cdp_point_popup]);

    const handleSubmit = (event) => {
        event.preventDefault();
        setSuccessMessage(undefined);
        setErrorMessage(undefined);

        // ── Validate synchronously using local variables ───────────────────────
        // DO NOT rely on state variables immediately after setState —
        // React batches state updates so they won't reflect until next render.
        // Instead, compute errors into local booleans first, then set state.
        const err_provider = TrainingProvider === null || TrainingProvider.trim() === "";
        const err_name     = ProgramName === null || ProgramName.trim() === "";
        const err_format   = ProgrameFormat === null || ProgrameFormat === "" || ProgrameFormat === undefined;
        const err_date     = ProgrameDate === null || ProgrameDate === "" || ProgrameDate === undefined;
        // Points: each category defaults to 0 and is optional on its own, but at
        // least one of the four must be greater than 0. Empty / blank counts as 0.
        const nEP = parseFloat(EP) || 0;
        const nPS = parseFloat(PS) || 0;
        const nPM = parseFloat(PM) || 0;
        const nSL = parseFloat(SL) || 0;
        const err_points = !(nEP > 0 || nPS > 0 || nPM > 0 || nSL > 0);

        // Update error state for UI highlighting
        setTrainingProvider_ER(err_provider);
        setProgramName_ER(err_name);
        setProgrameFormat_ER(err_format);
        setProgrameDate_ER(err_date);
        setPoints_ER(err_points);

        // Check local booleans — not state variables
        const hasErrors = err_provider || err_name || err_format || err_date
                        || err_points;

        if (hasErrors) return; // stop here — form not valid

        // ── All valid — submit ─────────────────────────────────────────────────
        setSomethingUpadating(true);

        const inquirData = {
            contact_id: props.email,
            data: {
                ep:                EP,
                ps:                PS,
                sl:                SL,
                pm:                PM,
                program_date:      ProgrameDate,
                program_format:    ProgrameFormat,
                training_provider: TrainingProvider,
                program_name:      ProgramName,
            }
        };

        axios.post(
            props.EngineURL + 'cpd_points/insert_cpd_points.php',
            inquirData,
            {
                headers: {
                    "Content-Type": "application/json",
                    Authorization: `Bearer ${props.userToken}`,
                },
                crossdomain: true,
            }
        )
        .then(function (response) {
            setTimeout(function () {
                if (response?.data?.status === 200) {
                    setSuccessMessage(response.data.message);
                    setErrorMessage(undefined);
                    // Refresh parent CPD list
                    if (typeof props.RefreshData === 'function') {
                        props.RefreshData();
                    }
                } else {
                    setErrorMessage(response?.data?.message || "Something went wrong. Please try again.");
                    setSuccessMessage(undefined);
                }
                setSomethingUpadating(false);
            }, 500);
        })
        .catch(function (error) {
            setSomethingUpadating(false);
            setSuccessMessage(undefined);

            if (error.response) {
                const msg = error.response.data?.message || error.response.data?.error;
                if (error.response.status === 403) {
                    setErrorMessage("Your session has expired. Please log in again.");
                } else if (error.response.status === 400) {
                    setErrorMessage(msg || "Invalid submission. Please check your details.");
                } else if (error.response.status === 500) {
                    setErrorMessage("A server error occurred. Please try again later.");
                } else {
                    setErrorMessage(msg || "Something went wrong. Please try again.");
                }
            } else if (error.request) {
                setErrorMessage("Network error. Please check your connection and try again.");
            } else {
                setErrorMessage("Something went wrong. Please try again.");
            }
        });
    };

    const ProgrameOption = [
        { label: "Live Event",          value: "Live Event"          },
        { label: "Recording",           value: "Recording"           },
        { label: "Self-paced Learning", value: "Self-paced Learning" },
        { label: "Other",               value: "Other"               },
    ];

    return (
        <>
            <Modal
                className="manually_points_modal signup_model"
                show={props.cdp_point_popup}
                onHide={() => props.set_cpd_point_popup(false)}
            >
                <Modal.Header closeButton className="border-0 pb-0" />
                <Modal.Body className="pt-0">
                    {SuccessMessage !== undefined
                        ? <h5>Your CPD points have been submitted successfully.</h5>
                        : <Modal.Title>Manually Add CPD Points</Modal.Title>
                    }

                    <div className="about_page_wrapper">
                        {SuccessMessage === undefined &&
                            <div className="wFormContainer">
                                <Form onSubmit={handleSubmit}>

                                    <Form.Group as={Col} md="12" xs="12" className="form-group">
                                        <Form.Label htmlFor="trainig_provider">Training provider *</Form.Label>
                                        <Form.Control
                                            className={TrainingProvider_ER ? 'form-error' : ''}
                                            type="text"
                                            id="trainig_provider"
                                            placeholder="Training Provider"
                                            value={TrainingProvider}
                                            disabled={SomethingUpadating}
                                            onChange={(e) => setTrainingProvider(e.target.value)}
                                        />
                                        {TrainingProvider_ER &&
                                            <Form.Text className="field-error-msg">Please enter the training provider name.</Form.Text>
                                        }
                                    </Form.Group>

                                    <Form.Group as={Col} md="12" xs="12" className="form-group">
                                        <Form.Label htmlFor="program_name">Program name *</Form.Label>
                                        <Form.Control
                                            className={ProgramName_ER ? 'form-error' : ''}
                                            type="text"
                                            id="program_name"
                                            placeholder="Program Name"
                                            value={ProgramName}
                                            disabled={SomethingUpadating}
                                            onChange={(e) => setProgramName(e.target.value)}
                                        />
                                        {ProgramName_ER &&
                                            <Form.Text className="field-error-msg">Please enter the program name.</Form.Text>
                                        }
                                    </Form.Group>

                                    <Form.Group as={Col} md="12" xs="12" className="form-group">
                                        <Form.Label htmlFor="program_format">Program format *</Form.Label>
                                        <Select
                                            className={ProgrameFormat_ER ? 'form-error' : ''}
                                            options={ProgrameOption}
                                            placeholder="Please select"
                                            isClearable={true}
                                            id="program_format"
                                            isDisabled={SomethingUpadating}
                                            onChange={(val) => setProgrameFormat(val ? val.value : "")}
                                        />
                                        {ProgrameFormat_ER &&
                                            <Form.Text className="field-error-msg">Please select a program format.</Form.Text>
                                        }
                                    </Form.Group>

                                    <Form.Group as={Col} md="12" xs="12" className="form-group">
                                        <Form.Label htmlFor="program_date">Program date *</Form.Label>
                                        <div className="cpd_date_wrapper">
                                            <img src={CalenderIcon} alt="" className="cpd_date_icon" />
                                            <Form.Control
                                                type="date"
                                                className={ProgrameDate_ER
                                                    ? 'date_filed form-error cpd_date_input'
                                                    : 'date_filed cpd_date_input'}
                                                id="program_date"
                                                value={ProgrameDate}
                                                disabled={SomethingUpadating}
                                                onChange={(e) => setProgrameDate(e.target.value)}
                                            />
                                        </div>
                                        {ProgrameDate_ER &&
                                            <Form.Text className="field-error-msg">Please select the program date.</Form.Text>
                                        }
                                    </Form.Group>

                                    <Form.Group as={Col} md="12" xs="12" className="form-group">
                                        <Form.Label htmlFor="cpd_ep">Ethics & Professional Responsibility points *</Form.Label>
                                        <Form.Control
                                            className={Points_ER ? 'form-error' : ''}
                                            type="number"
                                            id="cpd_ep"
                                            placeholder="0"
                                            min="0"
                                            value={EP}
                                            disabled={SomethingUpadating}
                                            onChange={(e) => setEP(e.target.value)}
                                        />
                                    </Form.Group>

                                    <Form.Group as={Col} md="12" xs="12" className="form-group">
                                        <Form.Label htmlFor="cpd_ps">Professional Skills points *</Form.Label>
                                        <Form.Control
                                            className={Points_ER ? 'form-error' : ''}
                                            type="number"
                                            id="cpd_ps"
                                            placeholder="0"
                                            min="0"
                                            value={PS}
                                            disabled={SomethingUpadating}
                                            onChange={(e) => setPS(e.target.value)}
                                        />
                                    </Form.Group>

                                    <Form.Group as={Col} md="12" xs="12" className="form-group">
                                        <Form.Label htmlFor="cpd_pm">Practice Management & Business Skills points *</Form.Label>
                                        <Form.Control
                                            className={Points_ER ? 'form-error' : ''}
                                            type="number"
                                            id="cpd_pm"
                                            placeholder="0"
                                            min="0"
                                            value={PM}
                                            disabled={SomethingUpadating}
                                            onChange={(e) => setPM(e.target.value)}
                                        />
                                    </Form.Group>

                                    <Form.Group as={Col} md="12" xs="12" className="form-group">
                                        <Form.Label htmlFor="cpd_sl">Substantive Law points *</Form.Label>
                                        <Form.Control
                                            className={Points_ER ? 'form-error' : ''}
                                            type="number"
                                            id="cpd_sl"
                                            placeholder="0"
                                            min="0"
                                            value={SL}
                                            disabled={SomethingUpadating}
                                            onChange={(e) => setSL(e.target.value)}
                                        />
                                    </Form.Group>

                                    {/* Group-level rule: at least one category must be greater than 0 */}
                                    {Points_ER &&
                                        <Form.Group as={Col} md="12" xs="12" className="form-group pt-0">
                                            <Form.Text className="field-error-msg">Please enter CPD points in at least one category.</Form.Text>
                                        </Form.Group>
                                    }

                                    {ErrorMessage !== undefined &&
                                        <Alert variant="danger">{ErrorMessage}</Alert>
                                    }
                                    {SomethingUpadating &&
                                        <Alert variant="primary">Sending…</Alert>
                                    }

                                    <Form.Group as={Col} xs="12" className="form-group cpd_modal_btns">
                                        <Button
                                            variant="primary"
                                            type="submit"
                                            className="cpd_save_btn"
                                            disabled={SomethingUpadating}
                                        >
                                            {SomethingUpadating ? 'Saving…' : 'Save'}
                                        </Button>
                                        <Button
                                            variant="secondary"
                                            type="button"
                                            className="btn-cancle"
                                            disabled={SomethingUpadating}
                                            onClick={() => props.set_cpd_point_popup(false)}
                                        >
                                            Cancel
                                        </Button>
                                    </Form.Group>

                                </Form>
                            </div>
                        }
                    </div>
                </Modal.Body>
            </Modal>
        </>
    );
};
